import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { LocaleLink as Link } from '../i18n/locale-link';
import { useT } from '../i18n/locale-context';
import { useAuth } from '../lib/auth-context';
import { useHydrated } from '../lib/use-hydrated';
import { track } from '../lib/funnel';
import { lazyRoute } from '../lib/lazy-route';
import {
  BID_WAITLIST_RETURN_PARAM,
  bidWaitlistRegisterPath,
  claimBidWaitlistReturn,
  stripBidWaitlistReturnFlag,
} from '../lib/bid-waitlist-return';
import {
  featureWaitlistQueryKey,
  useFeatureWaitlistState,
  type WaitlistState,
} from '../lib/use-feature-access';
import { CheckIcon } from './icons';

/**
 * The questionnaire sheet, loaded only when somebody can open it — a signed-in
 * reader whose questionnaire is not submitted yet. Guests and the prerendered
 * HTML never download it. `lazyRoute` for the same reason the bid-package panel
 * uses it: a deploy renames chunks, and a tab opened before one must recover
 * rather than show an error screen.
 */
const loadFormModal = () => import('./bid-waitlist-form-modal');
const BidWaitlistFormModal = lazy(
  lazyRoute('bid-waitlist-form', () =>
    loadFormModal().then((m) => ({ default: m.BidWaitlistFormModal })),
  ),
);

/** The padlock INSIDE the text run, not a sibling flex item: on a narrow phone
 *  the RU label wraps to two lines, and a sibling glyph then sits alone at the
 *  left edge while the text centres without it. Inline, it rides the first line
 *  and the pair stays one centred group. The lock is the same glyph
 *  «Открыть оригинал» wears right above this button (owner, 2026-09-28): the
 *  beta is closed, and the page says so the way it says it everywhere else. */
function CtaLabel({ text }: { text: string }) {
  return (
    <span>
      <span aria-hidden="true" className="mr-1.5">
        🔒
      </span>
      {text}
    </span>
  );
}

/** What an open sheet was opened as — frozen at the moment it opened, so the
 *  sheet's own events describe what the reader actually saw. */
interface OpenForm {
  /** A press from before the questionnaire existed is being completed. */
  reprompt: boolean;
  /** Opened by itself on the way back from /register or /login (`?bidpkg=1`). */
  afterAuth: boolean;
}

/**
 * «Подготовить тендерную документацию» — the public face of the closed
 * «Пакет заявки» beta: a waitlist with a questionnaire, not the feature.
 *
 * Owner, 2026-09-29: EVERY REGISTERED reader may fill in the questionnaire,
 * free or paying; a guest is sent to registration first. Who sees what:
 *  - the account HOLDS the grant (`granted`, i.e. the tender payload came back
 *    with `bidPackage: true`) → nothing here; the real panel further down the
 *    page is theirs. This component never imports that panel's chunk.
 *  - the lot is closed (status, or its deadline has passed) → nothing: a
 *    package for a lot nobody can bid on any more is not an offer.
 *  - guest → a link to `/register?next=<this lot>?bidpkg=1`
 *    (`bid-waitlist-return.ts`), tracked as `bid_waitlist_signup_clicked` —
 *    its own name, deliberately NOT `upgrade_cta_clicked`: this click goes to
 *    registration, not to a Pro wall, and must not inflate that funnel step.
 *    After registering (e-mail, code or Telegram) — or signing in instead — the
 *    reader is brought back here and the sheet OPENS BY ITSELF, once.
 *  - signed in (free or paying), questionnaire not submitted → a button that
 *    opens the QUESTIONNAIRE (`bid-waitlist-form-modal.tsx`) — a bottom sheet on
 *    a phone, a centred modal on a desktop. Submitting it is what puts the
 *    reader in the queue; the SERVER records `bid_package_waitlisted` and
 *    `bid_waitlist_answered`.
 *  - pressed BEFORE the questionnaire existed (`/auth/me` lists the key in
 *    `featureWaitlist` but not in `featureWaitlistAnswered`) → the same button,
 *    captioned «Дополните анкету»: since 2026-09-29 «в очереди» means the form
 *    was submitted, and the form is how the old press is completed.
 *  - the questionnaire is submitted (`featureWaitlistAnswered`, or a submission
 *    in this session) → a disabled «Вы в очереди» on every lot, captioned with
 *    the channel we will write to: Telegram when linked, otherwise e-mail. The
 *    same for free and paying — the free-only «Доступ открываем подписчикам
 *    Pro и Бизнес первыми · Тарифы →» variant is gone with the paying-only rule.
 *
 * ⚠️ Hydration. The prerendered HTML is the guest link, always: `status` is
 * `loading` under SSR, and a deadline check against «now» would bake the
 * prerender's clock into the page. Both are read only once `useHydrated()` is
 * true, which is the one idiom React guarantees to hydrate with the server
 * value first — no #418. The same reason `daysLeft` on this page is gated. The
 * guest link's `next` is built from the PATHNAME only, for the same reason.
 */
export function BidWaitlistButton({
  tenderId,
  granted,
  closed,
  deadline,
}: {
  tenderId: string;
  /** `data.bidPackage` — the account already holds the feature. */
  granted: boolean;
  /** The lot's status says it is finished (`statusKind === 'closed'`). */
  closed: boolean;
  /** Raw ISO deadline from the payload; compared with now on the client only. */
  deadline: string | null | undefined;
}) {
  const t = useT();
  const hydrated = useHydrated();
  const location = useLocation();
  const { status, user } = useAuth();
  const waitlist = useFeatureWaitlistState('bid_package');
  const queryClient = useQueryClient();
  const [form, setForm] = useState<OpenForm | null>(null);

  const authed = hydrated && status === 'authed';
  /**
   * Paying = an active, unexpired, non-free plan on `/auth/me` — the same test
   * `onboarding-modal.tsx` uses. It no longer decides WHETHER the form opens
   * (everyone registered gets it); it rides in the events as `paid`, so the
   * owner's funnel can be split by it.
   */
  const sub = user?.subscription;
  const paid = !!(sub && !sub.expired && sub.plan !== 'free');
  /** Who the questionnaire is for: any signed-in reader who has not submitted it. */
  const opensForm = authed && waitlist !== 'answered';
  const returning = new URLSearchParams(location.search).get(BID_WAITLIST_RETURN_PARAM) === '1';

  // Warm the sheet's chunk the moment it can be opened, so the press does not
  // wait on a download. A failure here is ignored: the press retries through
  // `lazyRoute`, which knows how to recover from a stale deploy.
  useEffect(() => {
    if (opensForm) void loadFormModal().catch(() => undefined);
  }, [opensForm]);

  /*
   * Back from /register or /login with `?bidpkg=1`: open the sheet by itself,
   * ONCE. Claimed per history entry, because the lot renders this button twice
   * (mobile block + desktop rail) and both see the flag in the same commit; the
   * first copy claims it, strips it from the address bar (a reload must not
   * reopen the form) and opens the sheet — which is portalled, so it shows
   * whichever copy owns it. Waits for the session: the flag is only consumed
   * once the reader is signed in. A submitted questionnaire, a granted account
   * or a finished lot consume it silently.
   */
  useEffect(() => {
    if (!returning || !authed) return;
    if (!claimBidWaitlistReturn(location.key)) return;
    stripBidWaitlistReturnFlag();
    const deadlineMs = deadline ? Date.parse(deadline) : NaN;
    const live = !granted && !closed && !(Number.isFinite(deadlineMs) && deadlineMs < Date.now());
    if (live && opensForm) setForm({ reprompt: waitlist === 'clicked', afterAuth: true });
  }, [returning, authed, location.key, granted, closed, deadline, opensForm, waitlist]);

  const closeForm = useCallback(() => setForm(null), []);
  const onSubmitted = useCallback(() => {
    // Every mounted copy of this button (mobile + desktop on the lot, and the
    // next lot the reader opens) reads this — see `featureWaitlistQueryKey`.
    const answered: WaitlistState = 'answered';
    queryClient.setQueryData(featureWaitlistQueryKey('bid_package'), answered);
  }, [queryClient]);

  if (granted || closed) return null;
  const deadlineMs = deadline ? Date.parse(deadline) : NaN;
  if (hydrated && Number.isFinite(deadlineMs) && deadlineMs < Date.now()) return null;

  const base =
    'inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md px-3 py-2.5 text-center text-[13px] font-semibold leading-tight transition-colors sm:px-4';
  /*
   * OUTLINED, like «Открыть оригинал» directly above it — not the filled brand
   * button it was until 2026-09-28. Measured over 14 days: the locked
   * «Открыть оригинал» drew 883 clicks from 773 people and is the one control
   * here that sells; this button — a closed beta's waitlist — was the filled,
   * brand-coloured, top-most control on the lot and drew 43 joins. Owner's
   * call: the source link first, this second, both outlined, both padlocked.
   */
  const primary = `${base} border border-edge bg-bone-surface text-ink hover:bg-bone-hover`;
  const captionClass = 'mt-1.5 text-center text-[12px] leading-snug text-ink-muted';

  /*
   * Rendered BESIDE whichever state the button is in, not inside the
   * "open the form" branch: a submission flips this component to «Вы в
   * очереди» while the sheet is still showing its success view, and a sheet
   * living inside the old branch would vanish mid-sentence.
   */
  const sheet = form ? (
    <Suspense fallback={null}>
      <BidWaitlistFormModal
        tenderId={tenderId}
        reprompt={form.reprompt}
        afterAuth={form.afterAuth}
        paid={paid}
        prefillTin={user?.tin}
        telegram={user?.telegramConnected === true}
        onClose={closeForm}
        onSubmitted={onSubmitted}
      />
    </Suspense>
  ) : null;

  if (!authed) {
    return (
      <div>
        <Link
          to={bidWaitlistRegisterPath(location.pathname)}
          onClick={() => track('bid_waitlist_signup_clicked', { tenderId })}
          className={primary}
        >
          <CtaLabel text={t('tender.bidWaitlist.cta')} />
        </Link>
        <p className={captionClass}>{t('tender.bidWaitlist.caption')}</p>
      </div>
    );
  }

  if (!opensForm) {
    // «Вы в очереди» — the questionnaire is submitted.
    return (
      <div>
        <button
          type="button"
          disabled
          className={`${base} cursor-default border border-status-active-dot/40 bg-status-active-bg text-status-active-text`}
        >
          <CheckIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-status-active-dot" />
          {t('tender.bidWaitlist.queued')}
        </button>
        <p className={captionClass}>
          {user?.telegramConnected
            ? t('tender.bidWaitlist.queuedCaption')
            : t('tender.bidWaitlist.queuedCaptionEmail')}
        </p>
        {sheet}
      </div>
    );
  }

  // «Дополните анкету» when the press predates the questionnaire, the plain
  // caption when the reader never pressed — free and paying alike.
  const caption =
    waitlist === 'clicked'
      ? t('tender.bidWaitlist.captionReprompt')
      : t('tender.bidWaitlist.caption');

  return (
    <div>
      <button
        type="button"
        onClick={() => setForm({ reprompt: waitlist === 'clicked', afterAuth: false })}
        className={primary}
      >
        <CtaLabel text={t('tender.bidWaitlist.cta')} />
      </button>
      <p className={captionClass}>{caption}</p>
      {sheet}
    </div>
  );
}
